/**
 * EVO — kirish ekrani.
 *
 * Klaviatura bilan to'liq ishlaydi (GAP_ANALYSIS §C3):
 * email maydoni avtofokusda, Tab — parolga, Enter — yuborish.
 * Sichqoncha umuman kerak emas.
 */
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { isApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useT } from '../lib/i18n-context';
import { AlightMark } from '../components/brand/alight-logo';
import { LangSwitch } from '../components/layout/lang-switch';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Spinner } from '../components/ui/spinner';

export function LoginPage({ redirectTo }: { readonly redirectTo?: string }): ReactNode {
  const t = useT();
  const navigate = useNavigate();
  const { login, user, isLoading: isRestoring } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Allaqachon kirgan bo'lsa — login ekrani ko'rsatilmaydi.
  useEffect(() => {
    if (!isRestoring && user) {
      void navigate({ to: (redirectTo ?? '/') as '/', replace: true });
    }
  }, [user, isRestoring, navigate, redirectTo]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);

    if (email.trim() === '' || password === '') {
      setError(t('auth.error.required'));
      return;
    }

    setIsSubmitting(true);
    try {
      await login(email, password);
      await navigate({ to: (redirectTo ?? '/') as '/', replace: true });
    } catch (err) {
      /*
       * 401 uchun umumiy matn: «email yoki parol noto'g'ri».
       * Qaysi biri xato ekanini AYTMAYMIZ — aks holda mavjud email'larni
       * tanlab olish (user enumeration) mumkin bo'ladi.
       */
      if (isApiError(err)) {
        setError(err.status === 401 ? t('auth.error.invalidCredentials') : err.message);
      } else {
        setError(t('common.unknownError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /*
   * ═══════════════════════════════════════════════════════════════════════
   * BITTA USTUN — FAQAT KIRISH
   * ═══════════════════════════════════════════════════════════════════════
   * Ilgari chapda alohida ustun turardi: «Xitoydan import → real tannarx →
   * ombor → sotuv → pul» zanjiri va ostida va'da matni. U dasturning
   * mazmunini tushuntirish uchun qo'yilgan edi.
   *
   * Egasi uni OLIB TASHLASHNI so'radi (2026-08-17) va bu to'g'ri qaror:
   * bu ekranni har kuni ertalab O'Z xodimlari ochadi. Ular dasturning
   * nima qilishini biladi — ularga kerak bo'lgani email, parol va til.
   * Reklama matni sotilmaydigan, ichki dasturda har kuni takrorlanadigan
   * ortiqcha to'siq bo'lib qolgandi.
   *
   * Ekran endi markazda: to'siqsiz forma, tepasida belgi va til
   * almashtirgichi. Ikki ustunli tuzilma saqlanmadi — chapi bo'sh
   * qolgan ikki ustun ekranni «buzilgan» ko'rsatardi.
   */
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4 py-10">
      <main className="w-full max-w-sm">
        {/*
          ╔══════════════════════════════════════════════════════════════════╗
          ║ TIL ALMASHTIRGICH — KIRISHDAN OLDIN, KIRISHDAN KEYIN EMAS        ║
          ║                                                                  ║
          ║ Bu ekranda tilga oid HECH NARSA yo'q edi. Ya'ni ruscha yoki      ║
          ║ inglizcha gapiradigan xodim tilni faqat TIZIMGA KIRGANDAN keyin  ║
          ║ (yuqori paneldan) o'zgartira olardi — kirish ekranining o'zini   ║
          ║ esa o'zbekcha o'qishga majbur edi. Aynan shu paytda unga eng     ║
          ║ kerakli ikki so'z — «Email» va «Parol» — tanish tilda emas.      ║
          ║                                                                  ║
          ║ Bu teshik haqida `lang-switch.tsx` ning O'Z izohi ogohlantirgan  ║
          ║ edi: «til almashtirish odatda ilovani BIRINCHI MARTA ochgan      ║
          ║ odamga kerak». Birinchi marta ochgan odam esa aynan shu yerda    ║
          ║ turadi va yuqori panelni umuman ko'rmaydi.                        ║
          ╚══════════════════════════════════════════════════════════════════╝

          ┌──────────────────────────────────────────────────────────────────┐
          │ NIMA UCHUN SARLAVHADAN YUQORIDA                                  │
          │                                                                  │
          │ Almashtirgich forma MATNINI o'zgartiradi. Tugmadan keyin         │
          │ qo'yilsa, odam avval tushunmagan matnni o'qib, keyin uni         │
          │ topardi; klaviatura bilan esa unga yetish uchun butun formani    │
          │ (email → parol → tugma) Tab bilan bosib o'tish kerak bo'lardi.   │
          │                                                                  │
          │ Sahifaning yuqori-o'ng BURCHAGI RAD ETILDI: 1920 px monitorda    │
          │ u forma markazidan ~700 px uzoqda tushardi va ko'z uni           │
          │ ko'rmasdi. Bu yerda esa u 384 px li ustunning ichida, ko'z       │
          │ zanjirining boshida.                                             │
          │                                                                  │
          │ `autoFocus` email maydonida QOLDI: har kuni kiradigan odam       │
          │ uchun til allaqachon tanlangan va kursorning tugmaga tushishi    │
          │ uni sekinlashtirardi. Tilni o'zgartirmoqchi bo'lgan odam bir     │
          │ marta Shift+Tab bosadi — u baribir sichqonchaga tegmaydi.        │
          └──────────────────────────────────────────────────────────────────┘

          Brend belgisi bilan BIR qatorda: qator boshida belgi, oxirida
          almashtirgich (`ml-auto`).

          `tone="surface"`: bu ekran oq/yorug' sirt, korpus emas. Sukut
          bo'yicha keladigan grafit variant bu yerda oq sahifa o'rtasidagi
          qora qutiga aylanardi (sabab va raqamlar `lang-switch.tsx` da).

          `min-h-10` — qator balandligi E'LON QILINADI, ichkaridan
          yig'ilmaydi: belgi 40 px, almashtirgich 36 px va balandlik
          ularning kattasiga bog'lanib qolmasligi kerak.
        */}
        {/*
          BELGI ENDI HAR DOIM KO'RINADI.
          Ilgari u `lg:hidden` edi: katta ekranda brend chap ustunda
          turardi va bu yerda takrorlanmasligi kerak edi. Chap ustun
          olib tashlangach, bu ekrandagi YAGONA brend belgisi shu
          bo'lib qoldi — yashirilsa, katta ekranda hech qanday brend
          qolmasdi.

          FAQAT EMBLEMA, yozuvsiz — yon menyu bilan bir xil qoida.
        */}
        <div className="mb-6 flex min-h-10 items-center gap-2">
          <AlightMark className="size-10 shrink-0" />
          <div className="ml-auto">
            <LangSwitch tone="surface" />
          </div>
        </div>

        <h1 className="text-2xl font-semibold tracking-tight text-ink">
          {t('auth.login.title')}
        </h1>
        <p className="mt-1 text-sm text-ink-muted">{t('auth.login.subtitle')}</p>

        {/* `noValidate` — brauzerning inglizcha validatsiya matnlari chiqmasin,
            xabarlar `t()` orqali o'zbekcha bo'lsin. */}
        <form onSubmit={(e) => void handleSubmit(e)} noValidate className="mt-6 flex flex-col gap-4">
          {error && <Alert tone="danger">{error}</Alert>}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="evo-email" required>
              {t('auth.email')}
            </Label>
            <Input
              id="evo-email"
              name="email"
              type="email"
              inputMode="email"
              autoComplete="username"
              // Kursor darhol shu yerda — foydalanuvchi sichqonchaga tegmaydi.
              autoFocus
              placeholder={t('auth.email.placeholder')}
              value={email}
              invalid={error !== null}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="evo-password" required>
              {t('auth.password')}
            </Label>
            <Input
              id="evo-password"
              name="password"
              type="password"
              autoComplete="current-password"
              placeholder={t('auth.password.placeholder')}
              value={password}
              invalid={error !== null}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {/* Yagona `submit` tugmasi — Enter shu tugmani bosadi. */}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Spinner />}
            {isSubmitting ? t('auth.submitting') : t('auth.submit')}
          </Button>
      </form>
      </main>
    </div>
  );
}
